import React from "react";

interface Duration {
  days: number;
  hours: number;
  minutes: number;
}

function minutesToDuration(totalMinutes: number): Duration {
  const days = Math.floor(totalMinutes / (24 * 60));
  const hours = Math.floor((totalMinutes % (24 * 60)) / 60);
  const minutes = totalMinutes % 60;
  return { days, hours, minutes };
}

function durationToMinutes(duration: Duration): number {
  return duration.days * 24 * 60 + duration.hours * 60 + duration.minutes;
}

export type DurationUnit = 'days' | 'hours' | 'minutes';

interface DurationPickerProps {
  value: number; // in minutes
  onChange: (value: number) => void;
  label?: string;
  disabled?: boolean;
  units?: DurationUnit[]; // which units to show, default: all
}

const DurationPicker: React.FC<DurationPickerProps> = ({ value, onChange, label, disabled, units = ['days', 'hours', 'minutes'] }) => {
  const duration = minutesToDuration(value);

  const handleInput = (field: keyof Duration, newValue: string) => {
    // Only allow whole numbers >= 0
    let val = parseInt(newValue, 10);
    if (isNaN(val) || val < 0) val = 0;
    // Clamp values for hours/minutes
    if (field === 'hours' && val > 23) val = 23;
    if (field === 'minutes' && val > 59) val = 59;
    if (field === 'days' && val > 30) val = 30;
    const updated = { ...duration, [field]: val };
    onChange(durationToMinutes(updated));
  };

  return (
    <div className="flex flex-col items-start">
      {label && <span className="mb-2 text-sm font-medium text-muted-foreground">{label}</span>}
      <div className="flex gap-2 items-center">
        {units.includes('days') && (
          <label className="flex items-center gap-1">
            <input
              type="number"
              min={0}
              max={30}
              step={1}
              value={duration.days}
              onChange={e => handleInput('days', e.target.value)}
              disabled={disabled}
              className="w-12 px-1 py-1 border rounded text-center"
              inputMode="numeric"
              pattern="[0-9]*"
            />
            <span>Days</span>
          </label>
        )}
        {units.includes('hours') && (
          <label className="flex items-center gap-1">
            <input
              type="number"
              min={0}
              max={23}
              step={1}
              value={duration.hours}
              onChange={e => handleInput('hours', e.target.value)}
              disabled={disabled}
              className="w-12 px-1 py-1 border rounded text-center"
              inputMode="numeric"
              pattern="[0-9]*"
            />
            <span>Hours</span>
          </label>
        )}
        {units.includes('minutes') && (
          <label className="flex items-center gap-1">
            <input
              type="number"
              min={0}
              max={59}
              step={1}
              value={duration.minutes}
              onChange={e => handleInput('minutes', e.target.value)}
              disabled={disabled}
              className="w-12 px-1 py-1 border rounded text-center"
              inputMode="numeric"
              pattern="[0-9]*"
            />
            <span>Minutes</span>
          </label>
        )}
      </div>
    </div>
  );
};

export default DurationPicker; 